<template>
	<div
		v-show="splitLine"
		class="resource-planning-split-line"
		:style="splitLineStyle"
	>
		<div class="resource-planning-split-date" :style="splitDateStyle">
			{{ splitLine?.dateLabel }}
		</div>
	</div>
</template>

<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref, computed } from 'vue';
import { type ResourcePlanningGanttConfig } from '../ResourcePlanningGantt/ResourcePlanningGanttConfig';
import { CHART_ITEM_TYPE } from '../../constants';
import { type Booking } from '@/generated/graphql';
import { useFormatter } from '@/composables';
import { isAfter, addDays, clamp, startOfDay } from 'date-fns';

interface ResourcePlanningBookingSplitInteractionProps {
	container: HTMLElement;
	config: ResourcePlanningGanttConfig;
}

interface SplitLineState {
	bookingId: Booking['id'];
	splitDate: Date;
	left: number;
	lineTop: number;
	lineHeight: number;
	dateLabel: string;
	dateTopRelative: number;
}

const { container, config } =
	defineProps<ResourcePlanningBookingSplitInteractionProps>();

const emit = defineEmits<{
	(
		e: 'split',
		payload: {
			bookingId: Booking['id'];
			splitDate: Date;
		}
	): void;
}>();

const { formatWeekdayShortDayMonth } = useFormatter();

const splitLine = ref<SplitLineState | null>(null);

const splitLineStyle = computed(() => ({
	position: 'fixed' as const,
	left: `${splitLine.value?.left ?? 0}px`,
	top: `${splitLine.value?.lineTop ?? 0}px`,
	height: `${splitLine.value?.lineHeight ?? 0}px`,
}));

const splitDateStyle = computed(() => ({
	position: 'absolute' as const,
	top: `${splitLine.value?.dateTopRelative ?? 0}px`,
}));

const handleMouseMove = (e: MouseEvent) => {
	splitLine.value = null;

	const taskId = config.gantt.locate(e);
	if (!taskId) {
		return;
	}

	const item = config.gantt.getTask(taskId);
	if (!item || item.item_type !== CHART_ITEM_TYPE.BOOKING) {
		return;
	}

	const project = config.gantt.getTask(item.parent ?? '');
	if (!config.canModifyProject(project)) {
		return;
	}

	const dataAreaRect = config.gantt.$task_data.getBoundingClientRect();
	const leftPositionOnChart = Math.abs(e.clientX - dataAreaRect.left);
	const cursorDate = config.gantt.dateFromPos(leftPositionOnChart);

	if (!cursorDate) {
		return;
	}

	// The booking end_date in the chart is +1 day from the actual end date
	const bookingActualEndDate = addDays(item.end_date, -1);

	// Need at least 2 days to be splittable
	if (!isAfter(bookingActualEndDate, item.start_date)) {
		return;
	}

	// Don't show split line on the first day
	const cursorDay = startOfDay(cursorDate);
	if (!isAfter(cursorDay, startOfDay(item.start_date))) {
		return;
	}

	// Clamp to valid range — the modal lets the user adjust further
	const minSplitDate = addDays(startOfDay(item.start_date), 1);
	const splitDay = clamp(cursorDay, {
		start: minSplitDate,
		end: bookingActualEndDate,
	});

	const taskNode = config.gantt.getTaskNode(taskId);
	if (!taskNode) {
		return;
	}

	const barRect = taskNode.getBoundingClientRect();
	splitLine.value = {
		bookingId: String(taskId),
		splitDate: splitDay,
		left: e.clientX,
		lineTop: dataAreaRect.top,
		lineHeight: dataAreaRect.height,
		dateLabel: formatWeekdayShortDayMonth(splitDay),
		dateTopRelative: barRect.bottom + 8 - dataAreaRect.top,
	};
};

const handleClick = () => {
	if (!splitLine.value) {
		return;
	}

	emit('split', {
		bookingId: splitLine.value.bookingId,
		splitDate: splitLine.value.splitDate,
	});

	splitLine.value = null;
};

const handleMouseLeave = () => {
	splitLine.value = null;
};

const handleScroll = () => {
	splitLine.value = null;
};

onMounted(() => {
	container.addEventListener('mousemove', handleMouseMove);
	container.addEventListener('click', handleClick, true);
	container.addEventListener('mouseleave', handleMouseLeave);
	window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
	container.removeEventListener('mousemove', handleMouseMove);
	container.removeEventListener('click', handleClick, true);
	container.removeEventListener('mouseleave', handleMouseLeave);
	window.removeEventListener('scroll', handleScroll);
});
</script>

<style lang="scss" scoped>
@import '../ResourcePlanning/resourceGanttVariables';

.resource-planning-split-line {
	width: 2px;
	background-color: $indigo-70;
	pointer-events: none;
	z-index: zResourceGantt('split-line');
	transform: translateX(-1px);
}

.resource-planning-split-date {
	font-weight: 600;
	color: $indigo-70;
	pointer-events: none;
	white-space: nowrap;
	transform: translateX(6px);
}
</style>
